<template>
  <!-- START : Scan Products Page -->
  <main>
    <!-- START : Loader & Error Message -->
    <WCSpinner v-if="!hasError && loading" position="center" size="xl" />
    <!-- END : Loader & Error Message -->

    <template>
      <platform-renderer>
        <!-- START : Mobile template -->
        <template v-slot:onMobile>
          <div>
            <!-- START : Scanner Component -->
            <WCScanner
              v-if="isScanAndGoMode && isScannerVisible"
              :displayHeader="!!$route.params.id"
              :isCameraInactive="isCameraInactive && !loading"
              @scanEnabled="enableCamera"
              @decode="readCode"
            />
            <!-- END : Scanner Component -->
            <!-- START: Product(s) view -->
            <div v-if="isCameraSupported">
              <!-- START : Product Overlay -->
              <WCDraggable
                :expandView="!!item"
                :hasCancel="!!item || !!items"
                @close="closeProductPopup"
                :aria-label="$t('dragContainer')"
              >
                <!-- START: Alternate Scancode / mulitple item select view -->
                <ItemFromMultipleView
                  v-if="items"
                  :item="items[0]"
                  :alternateIds="items.slice(1)"
                  @selectItem="selectItem"
                />
                <!-- END: Alternate Scancode / mulitple item select view -->
                <!-- START: Product overlay view -->
                <OverlayView
                  v-else
                  :item="item"
                  :itemNotInList="itemNotInList"
                  :shoppingList="shoppingList"
                  :activeShoppingList="activeShoppingList"
                  :priceEmbeddedLineItem="priceEmbeddedLineItem"
                  @removeShoppingList="removeShoppingList()"
                />
                <!-- END: Product overlay view -->
                <!-- END: Scan and shopping list -->
              </WCDraggable>
            </div>
            <!-- END: Product(s) view -->
          </div>
        </template>
        <!-- END : Mobile template -->
      </platform-renderer>
    </template>
  </main>
  <!-- END : Product Details Page -->
</template>

<script>
import axios from 'axios';
import { mapGetters, mapActions } from 'vuex';
import PlatformRenderer from '@/modules/platform/components/PlatformRenderer';
import FocusMixin from '@/mixins/FocusMixin';
import MediaDevicesMixin from '@/mixins/MediaDevicesMixin';
import ScanAndGoMixin from '@/modules/user/mixins/ScanAndGoMixin';
import WCSpinner from '@/components/WCSpinner/WCSpinner.vue';
import WCSimpleToast from '@/modules/toasts/components/WCSimpleToast/WCSimpleToast.vue';
import ToastService from '@/modules/toasts/services/ToastService';
import ModalService from '@/modules/modals/services/modal.service';
import WCConfirmationModal from '@/components/WCConfirmationModal/WCConfirmationModal.vue';
import { MODAL_EVENTS } from '@/constants/EventConstants';
import redirectFallbackHome from '@/router/redirects/redirect-fallback-home/redirect-fallback-home';
import ItemFromMultipleView from '@/views/ScanProduct/ItemFromMultipleView/ItemFromMultipleView.vue';
import WCDraggable from '@/components/WCDraggable/WCDraggable.vue';
import WCScanner from '../../components/WCScanner/WCScanner.vue';
import OverlayView from './OverlayView.vue';

let timeout;
let scanAndGoCheck;

export default {
  components: {
    PlatformRenderer,
    WCSpinner,
    WCScanner,
    OverlayView,
    WCDraggable,
    ItemFromMultipleView,
  },
  mixins: [FocusMixin, MediaDevicesMixin, ScanAndGoMixin], // Mixin for main content focus via routing
  data() {
    return {
      item: null,
      priceEmbeddedLineItem: null,
      priceEmbeddedData: null,
      items: null,
      loading: false,
      hasError: false,
      isCameraInactive: false,
      visibilityEvent: '',
      codeResult: null,
    };
  },
  provide() {
    return {
      selectProductManually: this.selectProductManually,
    };
  },
  mounted() {
    this.startIdleTimer();

    this.startScanAndGoInactiveTimer();

    // Initiate Visibility
    this.initiateVisibility();
  },
  created() {
    /**
     * If the logged in user directly enters scan page, initiate enter scan mode
     */
    if (
      this.$configuration.scanAndGoEnabled &&
      this.$route.meta.scanHomePage &&
      !this.isScanAndGoMode
    )
      this.enterScanAndGoMode();
    /**
     * If the route params available then product details will be fetched
     */
    if (this.$route.params.id) this.fetchProductDetails();
    if (this.$route.params.listId) {
      this.fetchActiveShoppingList();
    } else {
      this.navigateRouteToActiveShoppingList();
    }
  },
  beforeDestroy() {
    clearInterval(scanAndGoCheck);
    // Remove visibility listener
    if (this.visibilityEvent) {
      window.removeEventListener(this.visibilityEvent, this.visibilityChangeEvt);
    }
  },
  methods: {
    ...mapActions({
      updateActiveShoppingList: 'lists/updateActiveShoppingList',
      updateActiveShoppingListItems: 'lists/updateActiveShoppingListItems',
      setUserFromCookie: 'user/setUserFromCookie',
    }),

    // Visibility change
    initiateVisibility() {
      if ('hidden' in document) {
        this.visibilityEvent = 'visibilitychange';
      } else if ('mozHidden' in document) {
        this.visibilityEvent = 'mozvisibilitychange';
      } else if ('webkitHidden' in document) {
        this.visibilityEvent = 'webkitvisibilitychange';
      } else if ('msHidden' in document) {
        this.visibilityEvent = 'msvisibilitychange';
      } else if ('onfocusin' in document) {
        document.onfocusout = this.visibilityChangeEvt;
      } else {
        window.onpagehide = this.visibilityChangeEvt;
        window.onblur = this.visibilityChangeEvt;
      }

      if (this.visibilityEvent) {
        window.addEventListener(this.visibilityEvent, this.visibilityChangeEvt);
      }
    },

    // Visibility change events
    visibilityChangeEvt() {
      this.isCameraInactive = true;
    },

    /**
     * Method to get product details
     */
    navigateRouteToActiveShoppingList() {
      if (
        this.activeShoppingList &&
        Object.keys(this.activeShoppingList).length &&
        this.activeShoppingList.id
      ) {
        if (this.$route.params.id) {
          this.$router.push(`/scan/item/${this.$route.params.id}/${this.activeShoppingList.id}`);
        } else {
          this.$router.push(`/scan/list/${this.activeShoppingList.id}`);
        }
      }
    },

    resetData() {
      this.item = null;
      this.items = null;
      this.priceEmbeddedData = null;
      this.priceEmbeddedLineItem = null;
    },

    /**
     * Method to get product details
     */
    async fetchProductDetails() {
      this.loading = true;
      this.hasError = false;
      this.resetData();
      try {
        const response = await axios.get(`/api/i/q`, {
          params: {
            sc: this.$route.params.id,
            codeType: this.codeResult ? this.codeResult.format : '',
          },
        });

        if (response.data.embeddedPrice) {
          this.priceEmbeddedData = {
            embeddedPrice: response.data.embeddedPrice,
            embeddedDiscount: response.data.embeddedDiscount,
            expiration: response.data.expiration,
            originalPrice: response.data.originalPrice,
            embeddedNetWeight: response.data.embeddedNetWeight,
          };
        }

        const items = response?.data?.items;
        if (items?.length) {
          if (items.length > 1) {
            this.items = items;
          } else {
            this.item = response.data.items[0];
            this.handleItemScan();
          }
        } else {
          throw Error('No items in the response');
        }
      } catch (error) {
        this.resetData();
        this.hasError = true;
        this.isCameraInactive = true;
        this.openErrorModal();
      }
      this.loading = false;
    },
    handleItemScan() {
      if (this.priceEmbeddedData) {
        this.priceEmbeddedLineItem = {
          embeddedPrice: this.priceEmbeddedData.embeddedPrice,
          embeddedDiscount: this.priceEmbeddedData.embeddedDiscount,
          item: this.item,
          expiration: this.priceEmbeddedData.expiration,
          originalPrice: this.priceEmbeddedData.originalPrice,
          embeddedNetWeight: this.priceEmbeddedData.embeddedNetWeight,
        };

        this.priceEmbeddedData = null;
      }

      this.renderItemRestrictedModal();
    },

    /**
     * Method to open modal for close shopping list confirmation
     */
    async openErrorModal() {
      try {
        const response = await ModalService.open(WCConfirmationModal, {
          header: this.$t('scanError'),
          content: this.$t('errorScanProductNotFound'),
          confirm: this.$t('gotIt'),
        });
        if (response === MODAL_EVENTS.CONFIRM || response === MODAL_EVENTS.DISMISS) {
          this.removeScannedProduct();
        }
      } catch (error) {
        this.removeScannedProduct();
        console.error(error);
      }
    },

    /**
     * Method to get active shopping details
     */
    async fetchActiveShoppingList() {
      this.loading = true;
      try {
        const response = await axios.get(`/api/me/lists/${this.$route.params.listId}`);
        this.updateActiveShoppingListItems(response.data);
      } catch (error) {
        console.error(error);
        this.updateActiveShoppingListItems(null);
      }
      this.loading = false;
    },

    /**
     * Method to select the product manually
     */
    selectProductManually(data) {
      const activeShoppingList = this.activeShoppingList;
      const itemIndex = activeShoppingList.items.findIndex(i => i.item.id === data);
      const item = { ...activeShoppingList.items[itemIndex] };
      if (item.isSelected && !item.isManuallySelected) {
        item.isSelected = false;
        item.isManuallyUnSelected = true;
        activeShoppingList.items[itemIndex] = item;
        this.updateActiveShoppingList(activeShoppingList);
      } else if (
        (item.isSelected && item.isManuallySelected) ||
        (!item.isSelected && !item.isManuallySelected)
      ) {
        item.isManuallySelected = !item.isManuallySelected;
        item.isSelected = !item.isSelected;
        item.isManuallyUnSelected = false;
        activeShoppingList.items[itemIndex] = item;
        this.updateActiveShoppingList(activeShoppingList);
      }
    },

    /**
     * Method to update the shopping list completed
     */
    shoppingListCompleted() {
      ToastService.open(WCSimpleToast, {
        props: {
          variant: 'success',
          title: 'Shopping List',
          message: 'Shopping list is completed!',
        },
        timeout: 2000,
      });
    },

    /**
     * Method to update the scanned result
     */
    readCode(codeResult) {
      this.isCameraInactive = true;
      this.codeResult = codeResult;
      const listId = this.$route.params.listId;
      if (listId) {
        this.$router.push(`/scan/item/${codeResult.code}/${listId}`);
      } else {
        this.$router.push(`/scan/item/${codeResult.code}`);
      }
    },

    startIdleTimer() {
      clearTimeout(timeout);
      timeout = setTimeout(() => {
        this.isCameraInactive = true;
      }, 20000);
    },

    startScanAndGoInactiveTimer() {
      scanAndGoCheck = setInterval(async () => {
        const response = await axios.get('/api/scan-and-go');
        if (response.data.data === false) {
          await this.setUserFromCookie();
          await this.exitScanAndGoMode();
          await redirectFallbackHome(this.$router);
        }
      }, 60000);
    },

    enableCamera() {
      this.isCameraInactive = false;
      this.startIdleTimer();
    },

    /**
     * Method to render the modal for the restricted item
     */
    async renderItemRestrictedModal() {
      if (this.item.hasTimeRestrictions) {
        try {
          await ModalService.open(WCConfirmationModal, {
            headerIcon: 'ban',
            header: this.$t('itemProhibited'),
            content: this.$t('itemProhibitedMsg'),
            cancel: this.$t('okayGotIt'),
          });
        } catch (error) {
          console.error(error);
        }
      } else if (this.item.cashierApprovalRequired) {
        try {
          await ModalService.open(WCConfirmationModal, {
            headerIcon: 'triangle-exclamation',
            header: this.$t('cashierApproval'),
            content: this.$t('cashierApprovalMsg'),
            cancel: this.$t('okayGotIt'),
          });
        } catch (error) {
          console.error(error);
        }
      } else if (this.item.hasAgeRestriction) {
        try {
          await ModalService.open(WCConfirmationModal, {
            headerIcon: 'id-card',
            header: this.$t('idRequired'),
            content: this.$t('idRequiredMsg'),
            cancel: this.$t('okayGotIt'),
          });
        } catch (error) {
          console.error(error);
        }
      } else if (this.item.weightProfile && !this.priceEmbeddedLineItem) {
        try {
          await ModalService.open(WCConfirmationModal, {
            headerIcon: 'id-card',
            header: this.$t('weightedItem'),
            content: this.$t('weightedItemMsg'),
            cancel: this.$t('okayGotIt'),
          });
        } catch (error) {
          console.error(error);
        }
      } else if (this.item.hasRestriction) {
        try {
          await ModalService.open(WCConfirmationModal, {
            headerIcon: 'ban',
            header: this.$t('itemRestricted'),
            content: this.$t('itemRestrictedMsg'),
            cancel: this.$t('okayGotIt'),
          });
        } catch (error) {
          console.error(error);
        }
      }
    },

    /**
     * Method to remove the shopping list
     */
    removeScannedProduct() {
      this.resetData();
      this.hasError = false;
      if (this.$route.params.listId) {
        this.$router.push(`/scan/list/${this.$route.params.listId}`);
      } else {
        this.$router.push(`/scan`);
      }
    },

    /**
     * Method to remove the shopping list
     */
    removeShoppingList() {
      this.updateActiveShoppingList(null);
      if (this.$route.params.id) {
        this.$router.push(`/scan/item/${this.$route.params.id}`);
      } else {
        this.$router.push(`/scan`);
      }
    },
    selectItem(item) {
      this.items = null;
      this.item = item;
      this.handleItemScan();
    },
    /**
     * Method to reset the scanned result
     */
    closeProductPopup() {
      const listId = this.$route.params.listId;
      if (listId) {
        this.$router.push(`/scan/list/${listId}`);
      } else {
        this.$router.push(`/scan`);
      }
    },
  },
  watch: {
    /**
     * Method to update the product details on product id change in route
     */
    '$route.params.id': function() {
      if (this.$route.params.id) {
        this.fetchProductDetails();
      } else {
        this.enableCamera();
        this.resetData();
      }
    },

    /**
     * Method to update the product details on product id change in route
     */
    '$route.params.listId': function() {
      if (this.$route.params.listId) {
        this.fetchActiveShoppingList();
      } else {
        this.navigateRouteToActiveShoppingList();
      }
    },

    /**
     * Method to update the shopping list when cart items changes
     */
    cartItems() {
      if (this.activeShoppingList && Object.keys(this.activeShoppingList).length) {
        this.updateActiveShoppingList(this.activeShoppingList);
      }
    },

    /**
     * Method to display shopping list complete toast
     */
    isActiveShoppingListCompleted(newValue) {
      if (newValue) this.shoppingListCompleted();
    },

    /**
     * Method to update shopping list
     */
    getDetailShoppingList(newValue) {
      if (newValue && this.activeShoppingList && newValue.id === this.activeShoppingList.id) {
        this.updateActiveShoppingListItems(newValue);
      }
    },

    /**
     * Shopping list changes
     */
    shoppingListCount() {
      this.enableCamera();
    },

    isSyncing() {
      if (this.isSyncing) {
        this.isCameraInactive = false;
        this.startIdleTimer();
      }
    },
  },
  computed: {
    ...mapGetters({
      cartItems: 'cart/getLineItems',
      shoppingList: 'lists/getAvailableItemsList',
      activeShoppingList: 'lists/getActiveShoppingList',
      shoppingListCount: 'lists/getShoppingListCount',
      isActiveShoppingListCompleted: 'lists/getActiveShoppingListCompleted',
      getDetailShoppingList: 'lists/getDetailList',
      isScanAndGoMode: 'user/isCustomerModeScan',
      isScannerVisible: 'scan/isScannerVisible',
      isSyncing: 'cart/isSyncing',
    }),

    /**
     * Computed property to verify the item in active shopping list
     */
    itemNotInList() {
      if (
        this.activeShoppingList &&
        Object.keys(this.activeShoppingList).length &&
        this.item &&
        Object.keys(this.item).length
      ) {
        const itemDetail = this.activeShoppingList.items.find(i => i.item.id === this.item.id);
        return !itemDetail;
      }
      return false;
    },
    getItemRestrictions() {
      return '';
    },
  },
};
</script>
<style lang="scss" scoped>
@import '~@/assets/styles/tools';
@import '~@/assets/styles/settings';

/**
  *  Overlay to show the product details
  */
.scan-overlay {
  position: fixed;
  width: 100%;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #ffffff;
  border-radius: 1.25rem 1.25rem 0 0;
  z-index: 99;
  animation-name: slideUp 0.3s;
}

.product-overlay {
  height: 40%;
}

.close-container {
  position: absolute;
  top: 0;
  right: 0.25rem;
}

.close-icon {
  margin-top: 0.75rem;
  margin-right: 0.75rem;
}
</style>
